/**
 * Local memory of directed gestures the player has SENT (2026-06-08
 * producer note: "if I high five someone and they high-five me back, it
 * should just say 'X high-fived you back', with no button. The button
 * should only be there if someone else initiates it").
 *
 * <p>We can't tell a reply from a fresh gesture off the wire: the server
 * drops the {@code reciprocal} flag from its broadcast
 * ({@code ReactionEmittedEvent} carries only seat / reactionId / target /
 * time), and a bot's reply carries no flag at all. So the recipient has
 * no signal that an incoming high-five is a reply.
 *
 * <p>Instead we remember, on THIS client, when we last sent each gesture
 * to each seat. When a matching gesture then lands back FROM that seat
 * within {@link REPLY_WINDOW_MS}, it's a reply to us, and
 * {@link GestureToast} renders "...back" with no reciprocate button. Each
 * client only tracks its OWN sends, which is exactly right: the player
 * who initiated is the one who should see the "back" copy. Works
 * uniformly for bots (we record the gesture when we tap their avatar; the
 * bot's reply a beat later reads as a reply).
 *
 * <p>Purely local + best-effort: state lives only for the session and is
 * lost on reload. That's fine for a cosmetic toast. Read-only matching
 * (no consume) keeps {@link GestureToast}'s render side-effect-free; the
 * short window naturally expires stale records so a genuinely fresh
 * gesture from that seat shows the reciprocate button again.
 */

// `${seatId}:${reactionId}` -> epoch ms (local clock) of our latest send.
const sentAt = new Map<string, number>();

/**
 * How long after we send does an incoming matching gesture still read as
 * a reply. Bots reply ~1 beat later; a human taps within the sender's
 * toast lifetime (~4.2s) plus a round trip, so genuine replies land
 * within ~6s. 15s gives comfortable margin while keeping the window
 * small enough that an unrelated later gesture isn't mislabelled.
 */
const REPLY_WINDOW_MS = 15_000;

const keyFor = (seatId: number, reactionId: string): string =>
  `${seatId}:${reactionId}`;

/** Record that the local player just sent {@code reactionId} to {@code seatId}. */
export function rememberSentGesture(seatId: number, reactionId: string): void {
  sentAt.set(keyFor(seatId, reactionId), Date.now());
}

/**
 * True when an incoming {@code reactionId} from {@code seatId} is a reply
 * to a gesture we sent that same seat — i.e. we sent the same gesture to
 * them within {@link REPLY_WINDOW_MS}. Uses only the local clock (send
 * time vs now), so it's immune to client/server clock skew.
 */
export function isReplyToOurGesture(seatId: number, reactionId: string): boolean {
  const t = sentAt.get(keyFor(seatId, reactionId));
  if (t === undefined) return false;
  return Date.now() - t <= REPLY_WINDOW_MS;
}
